import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiAlertTriangle, 
  FiSearch, FiUsers, FiFileText, FiActivity, FiTool, 
  FiTarget, FiLock, FiTrendingUp, FiSettings, FiDollarSign,
  FiCheck, FiX, FiInfo, FiBriefcase
} = FiIcons;

function PurchaseAudit() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Kauft das bitte nicht – bevor ihr DAS getan habt! Ankaufsprüfung MFH",
    "alternativeHeadline": "Professionelle Ankaufsprüfung bei sanierungsbedürftigen Mehrfamilienhäusern",
    "image": "https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg",
    "author": {
      "@type": "Person",
      "name": "Maxim Kelm"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-AUSBAU",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-01-12",
    "dateModified": "2026-03-25",
    "description": "Ankaufsprüfung Mehrfamilienhaus: Warum hässliche Entlein top Deals sind und wie man Schadstoffe und Sanierungsstau richtig einpreist."
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Ankaufsprüfung Mehrfamilienhaus: Checkliste für Investoren"
        description="Mehrfamilienhaus kaufen? 🛑 Stopp! Erst prüfen: Schadstoffe, Brandschutz, Sanierungsstau. Unser Guide zur technischen Ankaufsprüfung."
        keywords="Ankaufsprüfung Immobilie, Mehrfamilienhaus kaufen, Sanierungsstau erkennen, Immobilien Gutachten, Bausubstanz prüfen, Due Diligence Bau"
        schema={articleSchema}
        canonical="/blog/ankaufspruefung-mehrfamilienhaus"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiSearch} />
              <span>Ankaufsprüfung & Sanierungsstrategie</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Kauft das bitte nicht – bevor ihr DAS getan habt!
            </h1>
            <p className="text-xl font-semibold text-mk-red-700 mb-8 text-left">
              Professionelle Ankaufsprüfung bei sanierungsbedürftigen Mehrfamilienhäusern
            </p>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Aktualisiert: März 2026</span>
            </div>
          </header>

          {/* Intro Video Stream */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl bg-black aspect-video relative">
            <video 
              src="https://canv.wasacon.com/mk-ausbau/_assets/video/6d93031f137eb76a3117f4e1ac71b858.mp4" 
              className="absolute top-0 left-0 w-full h-full object-cover"
              controls
              playsInline
              preload="metadata"
              poster="https://mx.ausbau.nrw/img/Trocken_und_Akustikbau_xl.jpg"
            >
              Ihr Browser unterstützt dieses Video-Format nicht.
            </video>
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            „Kauft das bitte nicht!“ – diesen Satz sollten gerade angehende Bestandsinvestoren deutlich öfter hören. Nicht, weil sie grundsätzlich nichts kaufen sollten, sondern weil sie häufig kaufen, bevor sie wirklich verstanden haben, was sie da erwerben.
          </div>

          <p className="text-gray-700 mb-8">
            In diesem Beitrag sind die wichtigsten Punkte zur <strong>Ankaufsprüfung bei sanierungsbedürftigen Gebäuden</strong> zusammengefasst – also genau den Objekten, die viele wegen „zu viel Aufwand“ links liegen lassen.
          </p>

          {/* Section 1: Hässliche Entlein */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="mr-3 text-mk-teal-600" />
            Warum „hässliche Entlein“ oft die besten Deals sind
          </h2>
          <p className="text-gray-700 mb-6">
            Stark sanierungsbedürftige, leerstehende und optisch wenig attraktive Gebäude mit hohem Investitionsbedarf wirken auf viele abschreckend. Für erfahrene Investoren können sie jedoch besonders interessant sein.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <h4 className="font-bold text-mk-teal-900 mb-2 flex items-center"><SafeIcon icon={FiUsers} className="mr-2"/> Wenig Wettbewerb</h4>
              <p className="text-sm text-gray-700 m-0">Auf frisch renovierte Objekte stürzen sich viele Käufer. Bei Gebäuden mit großem Sanierungsstau ist die Konkurrenz oft deutlich geringer.</p>
            </div>
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <h4 className="font-bold text-mk-teal-900 mb-2 flex items-center"><SafeIcon icon={FiDollarSign} className="mr-2"/> Guter Einstiegspreis</h4>
              <p className="text-sm text-gray-700 m-0">Alt, unansehnlich, feucht oder schadstoffbelastet – all das drückt den Kaufpreis spürbar.</p>
            </div>
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <h4 className="font-bold text-mk-teal-900 mb-2 flex items-center"><SafeIcon icon={FiTool} className="mr-2"/> Gestaltungsspielraum</h4>
              <p className="text-sm text-gray-700 m-0">Je größer der Sanierungsumfang, desto stärker kann das Gebäude technisch und optisch auf den eigenen Standard gebracht werden.</p>
            </div>
            <div className="bg-mk-teal-50 p-6 rounded-xl">
              <h4 className="font-bold text-mk-teal-900 mb-2 flex items-center"><SafeIcon icon={FiShield} className="mr-2"/> Langfristiger Bestand</h4>
              <p className="text-sm text-gray-700 m-0">Wird umfassend und fachgerecht saniert, steht das Objekt danach oft jahrzehntelang solide da.</p>
            </div>
          </div>
          <p className="text-gray-700 italic border-l-4 border-gray-300 pl-4">
            Klar ist aber auch: <strong>Solche Objekte sind nichts für Zocker oder Schönrechner.</strong> Sie sind etwas für Menschen, die bereit sind, gründlich zu prüfen, Risiken realistisch einzuschätzen und mit Profis zusammenzuarbeiten.
          </p>

          {/* Section 2: Das Team */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Nie alleine zur Besichtigung – dein Kernteam
          </h2>
          <p className="text-gray-700 mb-6">
            Selbst erfahrene Bauprofis gehen nicht allein in eine Ankaufsprüfung. Bei größeren Objekten ist ein Team aus mehreren Fachleuten sinnvoll.
          </p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-8">
            <ul className="divide-y divide-gray-100 m-0 p-0 list-none">
              <li className="p-4 hover:bg-gray-50 flex items-start">
                <div className="mr-4 text-2xl">☣️</div>
                <div>
                  <strong className="block text-gray-900 text-lg mb-1">Altlasten- / Schadstoffgutachter</strong>
                  <span className="text-gray-600 text-sm">Unverzichtbar bei Gebäuden vor etwa 1985. Asbest, PCB und andere Schadstoffe können nahezu überall verbaut sein.</span>
                </div>
              </li>
              <li className="p-4 hover:bg-gray-50 flex items-start">
                <div className="mr-4 text-2xl">🏗️</div>
                <div>
                  <strong className="block text-gray-900 text-lg mb-1">Statiker / Tragwerksplaner</strong>
                  <span className="text-gray-600 text-sm">Prüft Decken, Balken und tragende Bauteile. Erkennt, welche Umbauten überhaupt möglich sind.</span>
                </div>
              </li>
              <li className="p-4 hover:bg-gray-50 flex items-start">
                <div className="mr-4 text-2xl">🔥</div>
                <div>
                  <strong className="block text-gray-900 text-lg mb-1">Brandschutzexperte</strong>
                  <span className="text-gray-600 text-sm">Besonders wichtig in innerstädtischen Lagen. Bewertet Fluchtwege, Brandwände und Treppenräume.</span>
                </div>
              </li>
              <li className="p-4 hover:bg-gray-50 flex items-start">
                <div className="mr-4 text-2xl">⚖️</div>
                <div>
                  <strong className="block text-gray-900 text-lg mb-1">Rechtsexperte / Grundbuchprofi</strong>
                  <span className="text-gray-600 text-sm">Prüft Grundbuch, Geh- und Fahrrechte, Baulasten sowie den rechtlichen Rahmen.</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Section 3: Schadstoffe */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="mr-3 text-mk-red-600" />
            Altlasten & Schadstoffe: Der Spaßkiller – und Verhandlungstreiber
          </h2>
          <p className="text-gray-700 mb-6">
            Ein zentrales Thema bei älteren Gebäuden sind <strong>Schadstoffe</strong>. Es gibt kaum ein Bauteil – von Gipskarton über Estrich bis zu Klebern und Farben – in dem keine problematischen Stoffe stecken können.
          </p>
          
          <div className="bg-mk-red-50 border-l-4 border-mk-red-600 p-6 rounded-r-xl mb-8">
            <h3 className="text-mk-red-900 font-bold mt-0 mb-4">Warum das so wichtig ist:</h3>
            <p className="text-mk-red-800 m-0 text-sm leading-relaxed">
              Schadstoffe sind <strong>extrem teuer in der Sanierung</strong>. Häufig sind aufwendige Schutzmaßnahmen nötig: Einhausungen, Schleusen, Unterdruckhaltung, Atemschutz. Das ist kostspielig, zeitintensiv und streng reguliert.
            </p>
          </div>

          <p className="text-gray-700 mb-6">
            Und trotzdem sind solche Themen aus Käufersicht wertvoll. Denn sobald Schadstoffe fachlich nachgewiesen sind, muss der Verkäufer diese Information <strong>offenlegen</strong>. Das schafft eine starke Verhandlungsposition.
          </p>

          <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl mb-12">
            <h3 className="text-mk-teal-400 font-bold mt-0 mb-4">Strategie zur Kaufpreisminderung:</h3>
            <ol className="list-decimal list-inside space-y-3 m-0 p-0 text-gray-300">
              <li><strong>Schadstoffgutachten</strong> durch Fachleute erstellen lassen.</li>
              <li><strong>Sanierungskosten</strong> von Spezialfirmen kalkulieren lassen.</li>
              <li>Sicherheitspuffer einplanen.</li>
              <li><strong>Faustformel:</strong> <span className="text-white font-bold">Kaufpreis = Ausgangspreis – Sanierungskosten (inkl. Puffer)</span></li>
            </ol>
          </div>

          {/* Section 4: Feuchte Keller */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Feuchte Keller & Sanierungsstau – Risiko oder Chance?
          </h2>
          <p className="text-gray-700">
            Ein Klassiker, den man oft schon beim Betreten riecht: <strong>feuchte Keller</strong>. Für Laien ein Fluchtgrund, für Profis ein Verhandlungshebel. Es gibt viele Lösungen – von einfachen Abdichtungen und Horizontalsperren bis zu aufwendigen Systemen. Wartungsarme, robuste Lösungen sind oft langfristig die stressfreiere Wahl.
          </p>

          <div className="bg-orange-50 border border-orange-200 p-6 rounded-xl my-8">
            <h3 className="text-orange-900 font-bold mt-0 mb-3 flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="mr-2"/> 
              Der gefährlichere Fall: verdeckter Sanierungsstau
            </h3>
            <p className="text-orange-800 text-sm m-0 leading-relaxed">
              Noch kritischer als der „ehrlich hässliche“ Keller sind Gebäude mit <strong>teilweise sanierten Wohnungen in einem voll vermieteten Haus</strong>.
              <br/><br/>
              <strong>Das Problem:</strong> Der Rest kommt später. Wenn die übrigen Einheiten nach und nach saniert werden müssen, entstehen <strong>Strangsanierungen im laufenden Betrieb</strong>. Das bedeutet hohe Kosten, Zeitdruck und massive Belastung für die Mieter. Solche Objekte sind besonders riskant – vor allem, wenn sie nicht leerstehend übergeben werden.
            </p>
          </div>

          {/* Section 5: Brandschutz */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Brandschutz: Keine Spielwiese
          </h2>
          <p className="text-gray-700 mb-4">
            Brandschutz ist kein Nebenthema, sondern oft <strong>dealentscheidend</strong>. Schon kleine Abweichungen können teure oder praktisch unlösbare Anforderungen auslösen – etwa wenn Wohnungen geteilt werden sollen, aber nur ein Rettungsweg vorhanden ist.
          </p>
          <div className="flex bg-gray-50 p-4 rounded-lg border-l-4 border-mk-teal-500 mb-8">
            <SafeIcon icon={FiInfo} className="text-mk-teal-600 mt-1 mr-3 flex-shrink-0" />
            <div className="text-sm text-gray-700 italic">
              Hier entscheiden Details, Fachsprache und frühe Abstimmung mit Experten über Machbarkeit oder Deal-Abbruch.
            </div>
          </div>

          {/* Section 6: Behörden */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Mit Behörden sprechen – nicht davor weglaufen
          </h2>
          <p className="text-gray-700 mb-6">
            Man hört oft den Rat, Behörden nicht einzubeziehen, um „keine schlafenden Hunde zu wecken“. Professionelle Investoren gehen anders vor: Sie klären zentrale Fragen <strong>vor dem Kauf</strong>.
          </p>
          <div className="bg-mk-teal-50 p-6 rounded-xl mb-8">
            <h4 className="font-bold text-mk-teal-900 mb-4 mt-0">Wichtige Punkte:</h4>
            <ul className="space-y-3 list-none pl-0 m-0">
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="text-mk-teal-500 mr-2 mt-1"/> Was ist <strong>baurechtlich zulässig</strong>?</li>
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="text-mk-teal-500 mr-2 mt-1"/> Besteht <strong>Bestandsschutz</strong> – und wofür genau?</li>
              <li className="flex items-start"><SafeIcon icon={FiCheckCircle} className="text-mk-teal-500 mr-2 mt-1"/> Wo liegen technische Grenzen (z. B. Anschlussleistung Strom, Abwasser, Stellplätze)?</li>
            </ul>
          </div>
          <p className="text-gray-700">
            Wer früh Klarheit schafft, bekommt planbare Risiken – statt böser Überraschungen nach dem Notartermin.
          </p>

          {/* Section 7: Kosten & Mindset */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiDollarSign} className="mr-3 text-mk-teal-600" />
            Ankaufsprüfung kostet Geld – und spart ein Vermögen
          </h2>
          <p className="text-gray-700 mb-6">
            Ja, Gutachten kosten Geld – oft fünfstellige Beträge. Aber diese Kosten entstehen <strong>so oder so</strong>, spätestens nach dem Kauf. Durch die Vorverlagerung kaufst du dir:
          </p>
          <ul className="space-y-2 mb-6 list-disc pl-5 text-gray-700">
            <li>Klarheit</li>
            <li>eine realistische Kalkulationsgrundlage</li>
            <li>die Möglichkeit, rechtzeitig vom Kauf Abstand zu nehmen</li>
          </ul>
          <div className="text-center font-bold text-xl text-gray-900 my-8 italic">
            „Oft sind die besten Geschäfte die, die man bewusst nicht macht.“
          </div>

          {/* Section 8: Exklusivität */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            Exklusivität & professionelles Auftreten
          </h2>
          <p className="text-gray-700 mb-6">
            Wer viel Zeit und Geld in eine Prüfung investiert, sollte sich absichern – zum Beispiel über <strong>Exklusivitätsvereinbarungen</strong> oder Optionen. Für Verkäufer hat das ebenfalls Vorteile: Nach einer professionellen Prüfung wissen sie deutlich mehr über ihr eigenes Gebäude.
          </p>
          <p className="text-gray-700">
            Ein fachlich starkes Team vor Ort signalisiert außerdem: Hier steht kein Hoffnungsinvestor, sondern jemand, der strukturiert prüft und auf Basis von Fakten verhandelt.
          </p>

          {/* Conclusion */}
          <div className="bg-mk-teal-600 text-white p-10 rounded-[2.5rem] shadow-2xl mt-16 mb-12">
            <h2 className="text-white text-3xl font-bold mb-6 mt-0">Fazit: Kauft nicht blind – investiert in Klarheit</h2>
            <ul className="space-y-4 mb-8 list-none pl-0 text-mk-teal-50">
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> <strong>Hässliche Entlein können großartig sein</strong> – wenn man sie versteht.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> <strong>Nie allein prüfen</strong> – Statik und Schadstoffe gehören in Profi-Hände.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> <strong>Schadstoffe sind teuer</strong>, aber ein starker Verhandlungshebel.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> <strong>Behörden früh einbeziehen</strong>, statt sie zu meiden.</li>
              <li className="flex items-start"><SafeIcon icon={FiCheck} className="mr-2 mt-1"/> <strong>Geld für gute Prüfung</strong> spart im Zweifel Millionen.</li>
            </ul>
            <p className="text-white font-medium italic border-t border-mk-teal-400 pt-6">
              „Am Ende zählt nur eins: Der Kaufpreis muss zum realen Zustand und Risiko passen – und diese Risiken sollten vor dem Kauf so weit wie möglich reduziert werden.“
            </p>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Brauchen Sie Unterstützung bei der Ankaufsprüfung?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir begleiten Sie bei Besichtigungen und prüfen die technische Substanz Ihrer potenziellen Immobilie – von der Feuchtemessung bis zur Sanierungskalkulation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Kostenlose Erstberatung anfragen
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default PurchaseAudit;